import type { TFunction } from 'i18next';
import { Award, Calendar, Crown, Gem, Sparkles, Zap } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { BillingPlanCard } from './billingPlans';
import { CREDIT_PACKS, getPackGrantCredits, type CreditPack } from './creditPacks';
import { formatPackPrice } from '../utils/localePrice';

function packPeriodLine(packId: string, t: TFunction): string {
  if (packId === 'pack-monthly') return t('views.pricing.perMonth', 'per month');
  if (packId === 'pack-quarterly') return t('views.accountSettings.billing.period3Months', '3 months');
  return t('views.accountSettings.billing.period6Months', '6 months');
}

function packDescription(packId: string, t: TFunction): string {
  if (packId === 'pack-monthly') return t('views.pricing.planMonthlyDesc', 'Active job hunt — all features unlocked every month');
  if (packId === 'pack-quarterly') return t('views.pricing.planQuarterlyDesc', '3-month plan — perfect for a planned job search');
  return t('views.pricing.planHalfYearlyDesc', '6-month plan — for career changers & long-term seekers');
}

function packFeatures(pack: CreditPack, t: TFunction): BillingPlanCard['features'] {
  const credits = getPackGrantCredits(pack);
  const base = [
    {
      Icon: Zap,
      label: t('views.pricing.optimizationsTotal', {
        count: credits,
        defaultValue: '{{count}} optimizations total',
      }),
    },
    {
      Icon: Sparkles,
      label: t('views.accountSettings.billing.featureAllModelsQueue', 'All AI models + priority queue'),
    },
    {
      Icon: Calendar,
      label: t('views.accountSettings.billing.featureTrackerHistory', 'Full tracker & optimization history'),
    },
  ];
  if (pack.savePct) {
    base.push({
      Icon: Sparkles,
      label: t('views.pricing.savePct', { pct: '28', defaultValue: 'Save 28%' }),
    });
  }
  return base;
}

export function buildBillingPlanCards(t: TFunction, locale: string): BillingPlanCard[] {
  const freeFeatures: BillingPlanCard['features'] = [
    {
      Icon: Zap,
      label: t('views.pricing.lifetimeOptimizations', {
        count: 3,
        defaultValue: '3 lifetime optimizations',
      }),
    },
    {
      Icon: Sparkles,
      label: t('views.accountSettings.billing.featureBasicAts', 'Basic ATS scoring'),
    },
    {
      Icon: Calendar,
      label: t('views.accountSettings.billing.featureTryPace', 'Try at your own pace'),
    },
  ];

  return [
    {
      id: 'free',
      packId: null,
      name: t('views.pricing.planFree', 'Free'),
      description: t('views.accountSettings.billing.freeDesc', 'For trying out the platform'),
      priceLine: () => t('views.pricing.planFree', 'Free'),
      features: freeFeatures,
      accentClass: 'billing-plan-icon--neutral',
      planIcon: Sparkles,
    },
    ...CREDIT_PACKS.map((pack): BillingPlanCard => ({
      id: pack.id,
      packId: pack.id,
      name:
        pack.id === 'pack-monthly'
          ? t('views.pricing.planMonthly', 'Monthly')
          : pack.id === 'pack-quarterly'
            ? t('views.pricing.planQuarterly', 'Quarterly')
            : t('views.pricing.planHalfYearly', 'Half-Yearly'),
      description: packDescription(pack.id, t),
      priceLine: () => formatPackPrice(pack.id, locale),
      periodLine: packPeriodLine(pack.id, t),
      features: packFeatures(pack, t),
      badge: pack.popular
        ? t('views.pricing.mostPopular', 'Most popular')
        : pack.savePct
          ? t('views.pricing.savePct', { pct: '28', defaultValue: 'Save 28%' })
          : undefined,
      accentClass:
        pack.id === 'pack-monthly'
          ? 'billing-plan-icon--blue'
          : pack.id === 'pack-quarterly'
            ? 'billing-plan-icon--green'
            : 'billing-plan-icon--teal',
      planIcon:
        pack.id === 'pack-monthly'
          ? Gem
          : pack.id === 'pack-quarterly'
            ? Award
            : Crown,
    })),
  ];
}

export function useBillingPlanCards(): BillingPlanCard[] {
  const { t, i18n } = useTranslation();
  return useMemo(() => buildBillingPlanCards(t, i18n.language), [t, i18n.language]);
}
